import { type RefObject, useCallback } from 'react';
import type { Measurable } from '@radix-ui/rect';

import { EmbedSettingsPopover } from '@/components/shared/embed-settings-popover';
import { useBlockSettingsState } from '@/hooks/use-block-settings-state';
import { type GroupChildFloat, getBlockFloat } from '@/lib/blocks/layout/group-layout';
import { normalizeEmbedUrl } from '@/lib/embed-utils';
import { scheduleScrollToPageEditorBlock } from '@/lib/page-editor/scroll-to-block';
import { readString } from '@/lib/prop-utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import { getBlockUiKey, type PageBlock } from '@/types/block';

interface EmbedBlockSettingsModalProps {
  sectionId: string;
  block: PageBlock;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorRef?: RefObject<Measurable | null>;
  showFloatOption?: boolean;
}

export function EmbedBlockSettingsModal({
  sectionId,
  block,
  open,
  onOpenChange,
  anchorRef,
  showFloatOption = false,
}: EmbedBlockSettingsModalProps) {
  const updateBlock = usePageEditorStore((state) => state.updateBlock);
  const previewBlockPropsWithFloatLayout = usePageEditorStore(
    (state) => state.previewBlockPropsWithFloatLayout,
  );
  const cancelBlockPropsWithFloatLayoutPreview = usePageEditorStore(
    (state) => state.cancelBlockPropsWithFloatLayoutPreview,
  );

  const applyChange = useCallback(
    (value: Record<string, unknown> | null, commit: boolean) => {
      const props = value ?? {};
      if (commit) {
        updateBlock(sectionId, block.id, { props });
        return;
      }
      previewBlockPropsWithFloatLayout(sectionId, block.id, props);
    },
    [sectionId, block.id, previewBlockPropsWithFloatLayout, updateBlock],
  );
  const cancelPreview = useCallback(
    () => cancelBlockPropsWithFloatLayoutPreview(sectionId, block.id),
    [block.id, cancelBlockPropsWithFloatLayoutPreview, sectionId],
  );

  const {
    applyPreview,
    handleSave: saveSettings,
    handleCancel,
  } = useBlockSettingsState({
    open,
    currentValue: block.props || {},
    applyChange,
    cancelPreview,
  });

  const blockProps = block.props || {};
  const currentUrl = readString(blockProps.url) || '';
  const currentHtml = readString(blockProps.html) || '';
  const currentFloat = getBlockFloat(block);
  const blockUiKey = getBlockUiKey(block);

  const handleSave = useCallback(
    (url: string, html: string) => {
      saveSettings({ url: normalizeEmbedUrl(url), html });
    },
    [saveSettings],
  );

  const handleFloatChange = useCallback(
    (float: GroupChildFloat | null) => {
      if (float === currentFloat) return;
      applyPreview({ float });
      scheduleScrollToPageEditorBlock(blockUiKey);
    },
    [applyPreview, blockUiKey, currentFloat],
  );

  return (
    <EmbedSettingsPopover
      open={open}
      onOpenChange={onOpenChange}
      anchorRef={anchorRef}
      url={currentUrl}
      html={currentHtml}
      float={currentFloat}
      onFloatChange={showFloatOption ? handleFloatChange : undefined}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  );
}
